import { useEffect, useState } from "react";
import { Text, Pressable, ActivityIndicator, Image } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import styled from "styled-components/native";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useClub } from "@/contexts/ClubContext";
import { colors, radius, font } from "@/lib/theme";
import StumpsIcon from "@/components/StumpsIcon";

const SafeArea = styled(SafeAreaView)`
  flex: 1;
  background-color: ${colors.white};
`;

const Center = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
  padding-horizontal: 24px;
`;

const Title = styled.Text`
  font-size: 24px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  letter-spacing: -0.5px;
  margin-bottom: 8px;
  margin-top: 24px;
`;

const Subtitle = styled.Text`
  font-size: 15px;
  color: ${colors.inkSecondary};
  margin-bottom: 32px;
  text-align: center;
`;

const ClubList = styled.View`
  width: 100%;
  max-width: 400px;
  gap: 12px;
`;

interface ClubInfo {
  id: string;
  name: string;
  shortName: string;
  logoUrl?: string;
}

export default function PickClubScreen() {
  const { clubIds, switchClub } = useClub();
  const [clubs, setClubs] = useState<ClubInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadClubs() {
      const results: ClubInfo[] = [];
      for (const cid of clubIds) {
        const snap = await getDoc(doc(db, "clubs", cid));
        if (snap.exists()) {
          const data = snap.data();
          results.push({ id: cid, name: data.name, shortName: data.shortName || "", logoUrl: data.logoUrl });
        }
      }
      setClubs(results);
      setLoading(false);
    }
    if (clubIds.length > 0) loadClubs();
  }, [clubIds]);

  if (loading) {
    return (
      <SafeArea>
        <Center>
          <ActivityIndicator size="large" color={colors.brand} />
        </Center>
      </SafeArea>
    );
  }

  return (
    <SafeArea>
      <Center>
        <StumpsIcon size={40} />
        <Title>Choose a club</Title>
        <Subtitle>You're a member of multiple clubs.</Subtitle>

        <ClubList>
          {clubs.map((c) => (
            <Pressable
              key={c.id}
              onPress={() => switchClub(c.id)}
              style={({ pressed }) => [
                {
                  backgroundColor: colors.white,
                  borderWidth: 2,
                  borderColor: colors.line,
                  borderRadius: radius.lg,
                  paddingVertical: 20,
                  paddingHorizontal: 20,
                  flexDirection: "row" as const,
                  alignItems: "center" as const,
                  gap: 16,
                },
                pressed && { borderColor: colors.brand, backgroundColor: colors.brand50 },
              ]}
            >
              {c.logoUrl ? (
                <Image source={{ uri: c.logoUrl }} style={{ width: 44, height: 44, borderRadius: 10 }} />
              ) : (
                <Text style={{ fontSize: 28 }}>🏏</Text>
              )}
              <Text style={{ fontSize: 17, fontWeight: "700", color: colors.ink, flex: 1 }}>
                {c.name}
              </Text>
            </Pressable>
          ))}
        </ClubList>
      </Center>
    </SafeArea>
  );
}
